import React, { useState } from 'react';

import { withEventScope } from '@repo/event-processor';
import {
  Button,
  transparentTheme,
  ButtonTheme,
} from '@repo/ui/components/Buttons';
import DownloadIcon from '@repo/ui/components/Icon/DownloadIcon';
import LoadingIcon from '@repo/ui/components/Icon/LoadingIcon';
import { fireToast } from '@repo/ui/components/Toast';
import { H6 } from '@repo/ui/components/Typography';
import { themeStyle } from '@repo/ui/theming';

import { downloadFile } from '../../AiTools/utils';
import { CenteredSpinnerWrapper } from '../styles';

import { Tile, LoadingImage, Menu, Wrapper } from './styles';
import { ImageTileProps } from './types';

const buttonTheme: ButtonTheme = {
  ...transparentTheme,
  padding: '0',
  backgroundActiveColor: themeStyle.darkBackgroundAlt,
  activeColor: themeStyle.varBrandMedium,
  iconColor: themeStyle.darkInkMain,
  iconActiveColor: themeStyle.transparent,
};

const ImageTile: React.FC<ImageTileProps> = ({ image, onClick }) => {
  const label = image.label ?? 'Custom';

  const [srcLoading, setSrcLoading] = useState(true);

  const handleDownload = async (e: React.MouseEvent): Promise<void> => {
    e.stopPropagation();
    try {
      await downloadFile(image.src!, label);
    } catch (e) {
      fireToast({ label: 'Failed to download image', error: true });
      console.error(e);
    }
  };

  const handleClick = (): void => {
    if (image.isLoading || srcLoading) {
      return;
    }

    onClick();
  };

  return (
    <Tile isLoading={image.isLoading} onClick={handleClick}>
      <Wrapper>
        <LoadingImage
          alt={label}
          blur={image.isLoading && !!image.src}
          src={image.src ?? '/images/bgRemoverLoadingImage.png'}
          onLoad={(): void => setSrcLoading(false)}
        />
        {image.isLoading && (
          <CenteredSpinnerWrapper
            theme={{
              spinnerColor: themeStyle.varBlueMain,
            }}
          >
            <LoadingIcon height="24px" />
          </CenteredSpinnerWrapper>
        )}
        {!image.isLoading && (
          <Menu>
            <H6 color={themeStyle.inkBase}>{label}</H6>
            <Button
              icon={{ component: DownloadIcon, height: '16px' }}
              name="downloadImageButton"
              target="_blank"
              theme={buttonTheme}
              width="40px"
              onClick={handleDownload}
            />
          </Menu>
        )}
      </Wrapper>
    </Tile>
  );
};

export default withEventScope(ImageTile, { module: 'imageTile' });
